import { Check, Minus } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionHeading, SECTION_CLASS } from "./SectionHeading";

type Level = "yes" | "partial" | "no";
interface Cell { level: Level; text: string }

const COLUMNS = ["Typical model router", "Cloud AI platform", "GenAICore"] as const;

const ROWS: { capability: string; cells: [Cell, Cell, Cell] }[] = [
  {
    capability: "Many model providers behind one API",
    cells: [
      { level: "yes", text: "Yes" },
      { level: "partial", text: "Mostly the platform's own catalogue" },
      { level: "yes", text: "20+ providers" },
    ],
  },
  {
    capability: "Works with the official OpenAI and Anthropic SDKs",
    cells: [
      { level: "partial", text: "Typically OpenAI format" },
      { level: "partial", text: "Typically its own API and SDK" },
      { level: "yes", text: "OpenAI Chat, Responses and Anthropic Messages" },
    ],
  },
  {
    capability: "Hosted RAG knowledge bases and agents",
    cells: [
      { level: "no", text: "Typically no" },
      { level: "partial", text: "Yes, on its own API and models" },
      { level: "yes", text: "Yes, called as a model name on any provider" },
    ],
  },
  {
    capability: "Use your own provider keys",
    cells: [
      { level: "partial", text: "Varies" },
      { level: "partial", text: "Your account on that cloud" },
      { level: "yes", text: "Yes, including Bedrock, Azure OpenAI and Gemini" },
    ],
  },
  {
    capability: "Client accounts with your markup",
    cells: [
      { level: "no", text: "Typically no" },
      { level: "no", text: "Typically no" },
      { level: "yes", text: "Yes, on Agency plans" },
    ],
  },
  {
    capability: "Prepaid balance that stops at $0",
    cells: [
      { level: "partial", text: "Varies" },
      { level: "no", text: "Typically billed after use" },
      { level: "yes", text: "Yes" },
    ],
  },
  {
    capability: "Failover across providers",
    cells: [
      { level: "yes", text: "Typically yes" },
      { level: "partial", text: "Typically within the platform" },
      { level: "yes", text: "Yes, before the first byte" },
    ],
  },
];

function CellMark({ level }: { level: Level }) {
  if (level === "yes") {
    return (
      <span className="h-5 w-5 shrink-0 rounded-full bg-brand text-brand-foreground flex items-center justify-center">
        <Check className="h-3 w-3" strokeWidth={2.4} />
      </span>
    );
  }
  return (
    <span className={cn("h-5 w-5 shrink-0 rounded-full flex items-center justify-center", level === "partial" ? "bg-brand-soft" : "bg-surface-muted")}>
      <Minus className="h-3 w-3 text-muted-foreground" strokeWidth={2} />
    </span>
  );
}

export function Comparison() {
  return (
    <section id="compare" className={SECTION_CLASS}>
      <SectionHeading
        eyebrow="Compare"
        title="More than a router."
        accent="Not locked to one cloud."
        description="How GenAICore compares with the two kinds of products agencies usually combine. Individual products vary."
        className="mb-12 sm:mb-16"
      />

      <div className="rounded-card bg-card p-2 sm:p-4 overflow-x-auto">
        <table className="w-full min-w-[680px] text-[14px]">
          <thead>
            <tr className="text-left text-[13px] text-muted-foreground">
              <th className="font-normal p-4 w-[28%]">Capability</th>
              {COLUMNS.map((col) => (
                <th key={col} className={cn("font-normal p-4 w-[24%]", col === "GenAICore" && "text-foreground font-medium")}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {ROWS.map((row) => (
              <tr key={row.capability} className="border-t border-border/60">
                <th scope="row" className="text-left font-medium p-4 align-top">{row.capability}</th>
                {row.cells.map((cell, i) => (
                  <td key={COLUMNS[i]} className={cn("p-4 align-top", i === 2 ? "bg-surface-muted/60" : "text-muted-foreground")}>
                    <span className="flex items-start gap-2.5">
                      <CellMark level={cell.level} />
                      <span>{cell.text}</span>
                    </span>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
